import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  iamApi as iamHttpApi,
  type CreatePolicyRequest,
  type IamPolicyGrantRecord,
  type SearchPolicyGrantsParams,
  type UpdatePolicyRequest,
} from '@/api/iam.api';

type IamPrincipalType = 'USER' | 'GROUP';
type IamGrantStatus = 'ACTIVE' | 'REVOKED';
type IamPolicyType = 'SYSTEM' | 'CUSTOM';

interface IamGrant {
  id: string;
  principalType: IamPrincipalType;
  principalId: string;
  policyId: string;
  scopes: Array<{
    type: 'TENANT' | 'OWN';
    service: string;
  }>;
  status: IamGrantStatus;
  grantedAt: string;
  updatedAt: string;
}

interface IamGrantsQuery {
  page?: number;
  limit?: number;
  keyword?: string;
  principalId?: string;
  principalType?: IamPrincipalType;
  policyId?: string;
  policyType?: IamPolicyType;
  status?: IamGrantStatus;
}

interface PageResult<T> {
  items: T[];
  total: number;
}

const POLICY_QK = ['iam', 'policies'] as const;
const GRANT_QK = ['iam', 'grants'] as const;

function toGrantSearchParams(query: IamGrantsQuery): SearchPolicyGrantsParams {
  const limit = query.limit ?? 20;
  const page = query.page ?? 1;
  const searchTerm = query.principalId || query.keyword?.trim();
  return {
    limit,
    offset: String((page - 1) * limit),
    ...(searchTerm ? { search: searchTerm } : {}),
    ...(query.principalType
      ? { identity_type: query.principalType === 'USER' ? 'user' : 'user_group' }
      : {}),
    ...(query.policyId ? { policy_id: query.policyId } : {}),
    ...(query.policyType
      ? { policy_type: query.policyType === 'SYSTEM' ? 'System' : 'Custom' }
      : {}),
  };
}

function toIamGrant(record: IamPolicyGrantRecord): IamGrant | undefined {
  if (record.identity_type !== 'user' && record.identity_type !== 'user_group') return undefined;
  const timestamp = new Date(record.authorization_at * 1000).toISOString();
  return {
    id: JSON.stringify([record.identity_type, record.identity, record.policy_id]),
    principalType: record.identity_type === 'user' ? 'USER' : 'GROUP',
    principalId: record.identity,
    policyId: record.policy_id,
    scopes: record.authorization_range.map((range) => ({
      type: range.range.toLowerCase() === 'self' ? 'OWN' : 'TENANT',
      service: range.service,
    })),
    status: 'ACTIVE',
    grantedAt: timestamp,
    updatedAt: timestamp,
  };
}

export function useCreateIamPolicy() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: CreatePolicyRequest) => {
      const response = await iamHttpApi.createPolicy(body);
      if (response.code !== 0) {
        throw new Error(response.message || '创建权限策略失败');
      }
      return response;
    },
    retry: false,
    onSuccess: () => qc.invalidateQueries({ queryKey: POLICY_QK }),
  });
}

export function useUpdateIamPolicy() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: UpdatePolicyRequest) => {
      const response = await iamHttpApi.updatePolicy(body);
      if (response.code !== 0) {
        throw new Error(response.message || '更新权限策略失败');
      }
      return response;
    },
    retry: false,
    onSuccess: () => qc.invalidateQueries({ queryKey: POLICY_QK }),
  });
}

export function useDeleteIamPolicy() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const response = await iamHttpApi.deletePolicy(id);
      if (response && response.code !== 0) {
        throw new Error(response.message || '删除权限策略失败');
      }
      return response;
    },
    retry: false,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: POLICY_QK });
      qc.invalidateQueries({ queryKey: ['iam', 'grants'] });
      qc.invalidateQueries({ queryKey: ['iam', 'users'] });
      qc.invalidateQueries({ queryKey: ['iam', 'groups'] });
    },
  });
}

/** 只读：授权列表；用户组/用户/策略详情按对应字段过滤。 */
export function useIamGrants(query: IamGrantsQuery = {}) {
  return useQuery({
    queryKey: [...GRANT_QK, 'list', query] as const,
    queryFn: async (): Promise<PageResult<IamGrant>> => {
      if (query.status === 'REVOKED') return { items: [], total: 0 };
      const response = await iamHttpApi.searchPolicyGrants(toGrantSearchParams(query));
      if (response.code !== 0) {
        throw new Error(response.message || '查询策略授权记录失败');
      }
      const items = response.data.records.map(toIamGrant).filter((grant): grant is IamGrant => {
        if (!grant) return false;
        if (query.principalId && grant.principalId !== query.principalId) return false;
        return true;
      });
      return {
        items,
        total: query.principalId ? items.length : response.data.total,
      };
    },
  });
}

export function useRevokeIamGrant() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (grant: IamGrant) => {
      const response = await iamHttpApi.revokePolicyGrant({
        policy_ids: [grant.policyId],
        identities: [
          {
            identity_type: grant.principalType === 'USER' ? 'user' : 'user_group',
            identity: grant.principalId,
          },
        ],
      });
      if (response.code !== 0) {
        throw new Error(response.message || '撤销策略授权失败');
      }
      return response;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: GRANT_QK });
      qc.invalidateQueries({ queryKey: POLICY_QK });
    },
  });
}
